import { useContext, useEffect, useState } from "react";
import {
  MdArrowForward,
  MdCalendarToday,
  MdClose,
  MdExpandMore,
  MdImageSearch,
  MdPhotoCamera,
  MdZoomOutMap,
} from "react-icons/md";
import { AppContext } from "../App";
import { CDN_URL, getInlineLoader } from "../Helpers";
import fallbackImage from "../assets/banner-removebg-preview.png";
import heroImage from "../assets/tuzo2.png";
import Footer from "../ones/Footer";

const PAGE_SIZE = 10;

function formatGalleryDate(timestamp) {
  if (!timestamp) {
    return "Recently added";
  }

  const date =
    String(timestamp).length <= 10
      ? new Date(Number(timestamp) * 1000)
      : new Date(timestamp);

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export default function GalleryLanding() {
  const appContext = useContext(AppContext);
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadGallery() {
      setLoading(true);
      const result = await appContext.getAllGallery({
        limit: PAGE_SIZE,
        withMeta: true,
      });
      if (mounted) {
        setGalleryItems(result.records);
        setHasMore(result.hasMore);
        setLoading(false);
      }
    }

    loadGallery();

    return () => {
      mounted = false;
    };
  }, []);

  async function viewMore() {
    setLoadingMore(true);
    const result = await appContext.getAllGallery({
      limit: PAGE_SIZE,
      offset: galleryItems.length,
      withMeta: true,
    });

    setGalleryItems((current) => [...current, ...result.records]);
    setHasMore(result.hasMore);
    setLoadingMore(false);
  }

  useEffect(() => {
    if (selectedItem) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [selectedItem]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setSelectedItem(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const heroSource = galleryItems[0]?.photo
    ? CDN_URL + galleryItems[0].photo
    : heroImage;

  return (
    <>
      <section className="gallery-page-hero">
        <div className="container">
          <div className="gallery-page-hero-inner">
            <div className="gallery-page-hero-copy" data-aos="fade-up">
              <p className="home-banner-kicker">Portfolio</p>
              <h1 className="gallery-page-title font-bold">
                Explore Our Custom Creations
              </h1>
              <p className="gallery-page-lead">
                A showcase of personalized gifts, awards, branded products, and
                creative pieces designed to celebrate moments, honor
                achievements, and bring ideas to life.
              </p>

              <div className="gallery-page-hero-actions">
                <button
                  type="button"
                  className="gallery-page-btn"
                  onClick={() => appContext.navTo({ item: "contact_us" })}
                >
                  Start a Project
                  <MdArrowForward size={20} />
                </button>
                <span className="gallery-page-count-pill">
                  <MdPhotoCamera size={20} />
                  {galleryItems.length} featured works
                </span>
              </div>
            </div>

            <div className="gallery-page-hero-visual" data-aos="fade-left">
              <img
                src={heroImage}
                alt="Esmeriser branded product collection"
                className="gallery-page-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="gallery-page-section">
        <div className="container">
          <div className="gallery-page-heading" data-aos="fade-up">
            <p className="home-banner-kicker">Selected Work</p>
            <h2 className="home-service-title font-bold">Gallery Highlights</h2>
            <p>
              Bright product moments, branded details, and finished pieces from
              the kind of work customers can build with Esmeriser.
            </p>
          </div>

          {loading ? (
            <div className="mSupportLoading" style={{ minHeight: 360 }}>
              {getInlineLoader()}
            </div>
          ) : galleryItems.length > 0 ? (
            <>
              <div className="gallery-page-grid">
                {galleryItems.map((item, index) => (
                  <article
                    className= "gallery-page-card"
                    /* {`gallery-page-card ${
                      index === 0 || index === 3
                        ? "gallery-page-card-featured"
                        : ""
                    }`}*/ 
                    key={item.uuid || item.id}
                    data-aos="fade-up"
                    data-aos-delay={(index % 4) * 70}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedItem(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedItem(item);
                      }
                    }}
                  >
                    <div className="gallery-page-card-media">
                      <img
                        src={item.photo ? CDN_URL + item.photo : fallbackImage}
                        alt={item.title || item.description || "Gallery item"}
                      />

                      <span className="gallery-page-card-view">
                        <MdZoomOutMap size={18} />
                      </span>
                    </div>
                    <div className="gallery-page-card-body">
                      <div>
                        <h3 className="font-bold">
                          {item.title || "Gallery Item"}
                        </h3>
                        <p>
                          {item.description ||
                            "A recent Esmeriser portfolio highlight."}
                        </p>
                      </div>
                      <span className="gallery-page-card-date">
                        <MdCalendarToday size={15} />
                        {formatGalleryDate(item.dateCreated || item.date)}
                      </span>
                    </div>
                  </article>
                ))}
              </div>

              {hasMore ? (
                <div className="page-view-more-row">
                  <button
                    type="button"
                    className="page-view-more-btn"
                    onClick={viewMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : "View More"}
                    <MdExpandMore size={22} />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="home-empty-state">
              <h3 className="font-bold">Gallery work is coming soon.</h3>
              <p>
                Published gallery records from the admin panel will appear here.
              </p>
            </div>
          )}
        </div>
      </section>

      {selectedItem ? (
        <div
          className="gallery-page-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={
            selectedItem.title || selectedItem.description || "Gallery item"
          }
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="gallery-page-lightbox-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="gallery-page-lightbox-close"
              onClick={() => setSelectedItem(null)}
              aria-label="Close gallery preview"
            >
              <MdClose size={24} />
            </button>

            <div className="gallery-page-lightbox-media">
              <img
                src={
                  selectedItem.photo
                    ? CDN_URL + selectedItem.photo
                    : fallbackImage
                }
                alt={
                  selectedItem.title ||
                  selectedItem.description ||
                  "Gallery item"
                }
              />
            </div>

            <div className="gallery-page-lightbox-copy">
              <div>
                <span className="gallery-page-lightbox-label">
                  <MdImageSearch size={18} />
                  Gallery Item
                </span>
                <h3 className="font-bold">
                  {selectedItem.title || "Gallery Item"}
                </h3>
                <p>
                  {selectedItem.description ||
                    "A recent Esmeriser portfolio highlight."}
                </p>
              </div>
              <span className="gallery-page-lightbox-date">
                <MdCalendarToday size={16} />
                Added{" "}
                {formatGalleryDate(
                  selectedItem.dateCreated || selectedItem.date,
                )}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </>
  );
}
